import _toConsumableArray from '@babel/runtime/helpers/toConsumableArray';
import _extends from '@babel/runtime/helpers/extends';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import * as React from 'react';
import { ResizeObserverPolyfill } from '../../util/polyfills.js';
import { format } from '../../util/stringFormatter.js';
import { createTimer } from '../../util/timer.js';
import { createDebouncedFunction } from '../../util/useDebounce.js';
import { isTouchDevice } from '@drone-js/media-query';
import { Box } from '../Box/Box.js';
import { fadeOption } from './helpers/fadeGradiant.js';
import { pageCountLocation } from './helpers/pageCountLocation.js';
import { Navigator as NavigatorMemoized } from '../Navigator/Navigator.js';
import { PageControl } from '../PageControl/PageControl.js';
import { PageCount } from '../PageCount/PageCount.js';
import { useExperimentContext } from '../../contexts/session/Experiment.js';
import { useDirectionContext, Direction } from '../../contexts/session/Locale.js';
import { useMediaQueryContext } from '../../contexts/system/MediaQuery.js';
import { CarouselStyled } from './Carousel.styled.js';
import { CarouselCard as CarouselCardMemoized } from './CarouselCard.js';
import { scrollSnapNoneClass } from './CarouselChild.styled.js';

var _excluded = ["accessibilityLabel", "accessibilityNextNavLabel", "accessibilityPageNumberLabel", "accessibilityPreviousNavLabel", "accessibilityItemNumberWithCountLabel", "cardWidth", "children", "currentCard", "display", "fade", "loading", "nextNavAttributes", "onScroll", "onPageChange", "pageButtons", "pageControl", "pageCount", "pageWidth", "previousNavAttributes", "scrollPadding", "cardSpacing", "height"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var navigatorWidth = 52;
var resizeDebounce = 300;
var scrollDebounce = 50;
var scrollEndTimer = 50;
var TOUCH_MOVE_THRESHOLD = 10; // Threshold of move to be considered swipe

var Carousel = function Carousel(_ref) {
  var accessibilityLabel = _ref.accessibilityLabel,
    accessibilityNextNavLabel = _ref.accessibilityNextNavLabel,
    accessibilityPageNumberLabel = _ref.accessibilityPageNumberLabel,
    accessibilityPreviousNavLabel = _ref.accessibilityPreviousNavLabel,
    accessibilityItemNumberWithCountLabel = _ref.accessibilityItemNumberWithCountLabel,
    cardWidth = _ref.cardWidth,
    children = _ref.children,
    currentCard = _ref.currentCard,
    _ref$display = _ref.display,
    display = _ref$display === void 0 ? 'list' : _ref$display,
    fade = _ref.fade,
    _ref$loading = _ref.loading,
    loading = _ref$loading === void 0 ? 'lazy' : _ref$loading,
    nextNavAttributes = _ref.nextNavAttributes,
    onScroll = _ref.onScroll,
    onPageChange = _ref.onPageChange,
    _ref$pageButtons = _ref.pageButtons,
    pageButtons = _ref$pageButtons === void 0 ? 'inside' : _ref$pageButtons,
    pageControl = _ref.pageControl,
    pageCount = _ref.pageCount,
    _ref$pageWidth = _ref.pageWidth,
    pageWidth = _ref$pageWidth === void 0 ? 'auto' : _ref$pageWidth,
    previousNavAttributes = _ref.previousNavAttributes,
    scrollPadding = _ref.scrollPadding,
    _ref$cardSpacing = _ref.cardSpacing,
    cardSpacing = _ref$cardSpacing === void 0 ? '8' : _ref$cardSpacing,
    _ref$height = _ref.height,
    heightProps = _ref$height === void 0 ? 'content-fit' : _ref$height,
    rest = _objectWithoutProperties(_ref, _excluded);
  var direction = useDirectionContext();
  var mediaQuery = useMediaQueryContext();
  var hasTouchSupport = isTouchDevice();
  var isTouchActiveRef = React.useRef(false);
  var touchStartXRef = React.useRef(0);
  var isScrollingRef = React.useRef(false);
  var scrollEndTimerRef = React.useRef(null);
  var isRtl = React.useMemo(function () {
    return direction === Direction.RTL;
  }, [direction]);
  var isSingle = React.useMemo(function () {
    return display === 'single';
  }, [display]);
  var hasNavigator = React.useMemo(function () {
    return !hasTouchSupport && !!pageButtons;
  }, [hasTouchSupport, pageButtons]);
  var scrollOffset = React.useMemo(function () {
    if (isSingle || !hasNavigator && !scrollPadding) return 0;
    return hasNavigator || !scrollPadding ? navigatorWidth : scrollPadding;
  }, [hasNavigator, isSingle, scrollPadding]);
  var _React$useState = React.useState(0),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    index = _React$useState2[0],
    setIndex = _React$useState2[1];
  var _React$useState3 = React.useState(1),
    _React$useState4 = _slicedToArray(_React$useState3, 2),
    length = _React$useState4[0],
    setLength = _React$useState4[1];
  var _React$useState5 = React.useState(false),
    _React$useState6 = _slicedToArray(_React$useState5, 2),
    backwardVisible = _React$useState6[0],
    setBackwardVisibility = _React$useState6[1];
  var _React$useState7 = React.useState(false),
    _React$useState8 = _slicedToArray(_React$useState7, 2),
    forwardVisible = _React$useState8[0],
    setForwardVisibility = _React$useState8[1];
  var backward = React.useMemo(function () {
    return !!index;
  }, [index]);
  var forward = React.useMemo(function () {
    return index < length - 1;
  }, [index, length]);
  var setNavigatorVisibility = React.useCallback(function (pageIndex, pageLength) {
    if (!hasNavigator) return;
    setBackwardVisibility(pageIndex > 0);
    setForwardVisibility(pageIndex < pageLength - 1);
  }, [hasNavigator]);
  var carouselRef = React.useRef();
  var outerCarouselRef = React.useRef(null);
  var childrenRef = React.useRef([]);
  var height = heightProps === 'parent-fit' ? '100%' : undefined;
  var cardRef = React.useRef();
  var _React$useState9 = React.useState([false]),
    _React$useState0 = _slicedToArray(_React$useState9, 2),
    hiddenCards = _React$useState0[0],
    setHiddenCards = _React$useState0[1];
  var cardScrollToRef = React.useRef();
  var cardsScrollLeftRef = React.useRef([0]);
  var cardsPageRef = React.useRef([0]);
  var snapOffsetsRef = React.useRef([0]);
  var pagesScrollLeftRef = React.useRef([0]);
  var lastScrollLeftRef = React.useRef(0);
  var hasCarousel = !!carouselRef.current;
  var _useExperimentContext = useExperimentContext(),
    incorrectInertAttrOnCarouselWhenResizing = _useExperimentContext.incorrectInertAttrOnCarouselWhenResizing;
  var _React$useReducer = React.useReducer(function (state, entries) {
      if (!childrenRef.current.length) return state;
      return entries.reduce(function (transientState, entry) {
        if (!entry.isIntersecting) return transientState;
        var childIndex = childrenRef.current.indexOf(entry.target);
        if (childIndex < 0 || state[childIndex]) return transientState;
        var newState = transientState || Array.from(state);
        newState[childIndex] = true;
        return newState;
      }, undefined) || state;
    }, [true, true]),
    _React$useReducer2 = _slicedToArray(_React$useReducer, 2),
    inView = _React$useReducer2[0],
    setIntersection = _React$useReducer2[1];
  var createChildRef = React.useCallback(function (childIndex, element) {
    if (element) childrenRef.current[childIndex] = element;
  }, []);
  var carouselChildren = React.useMemo(function () {
    var lastChildrenIdx = React.Children.count(children) - 1;
    return React.Children.map(children, function (child, childIndex) {
      var ariaLabel = accessibilityItemNumberWithCountLabel ? format(accessibilityItemNumberWithCountLabel, childIndex + 1, lastChildrenIdx + 1) : undefined;
      return /*#__PURE__*/React.createElement(CarouselCardMemoized, _extends({
        ref: function ref(element) {
          return createChildRef(childIndex, element);
        },
        minWidth: cardWidth,
        maxWidth: cardWidth,
        fullWidth: isSingle,
        inView: !isSingle || inView[childIndex]
      }, !isSingle && _objectSpread({
        mx: cardSpacing
      }, childIndex === lastChildrenIdx && {
        me: '0'
      }), {
        pb: "16",
        scrollSnapAlign: childIndex < lastChildrenIdx ? 'start' : 'end',
        scrollSnapStop: "always",
        isHiddenCard: childIndex < hiddenCards.length ? hiddenCards[childIndex] : false,
        ariaLabel: ariaLabel
      }), child);
    });
  }, [cardWidth, children, createChildRef, inView, isSingle, cardSpacing, hiddenCards, accessibilityItemNumberWithCountLabel]);
  var computePagination = React.useCallback(function () {
    var carousel = carouselRef.current;
    if (!carousel) return;
    var width = carousel.clientWidth,
      carouselOffset = carousel.offsetLeft,
      scrollWidth = carousel.scrollWidth,
      scrollLeft = carousel.scrollLeft;
    var widthOffset = +(hasNavigator && scrollOffset);
    var directionSign = -isRtl || +1;
    var end = (isRtl ? width - scrollWidth : scrollWidth) + carouselOffset;
    var scrollMax = (scrollWidth - width) * directionSign;
    var cards = carousel.children;
    var cardPageIndex = 0;
    var pageIndex = 0;
    var pageOffset = 0;
    cardsScrollLeftRef.current = [0];
    cardsPageRef.current = [0];
    snapOffsetsRef.current = [0];
    pagesScrollLeftRef.current = [0];
    for (var cardIndex = 1; cardIndex < cards.length; cardIndex += 1) {
      var card = cards[cardIndex];
      if (!cardIndex || !(card instanceof HTMLElement)) return;
      var offsetLeft = card.offsetLeft,
        offsetWidth = card.offsetWidth;
      var cardLeft = offsetLeft - carouselOffset;
      var cardRight = cardLeft + offsetWidth;
      var cardOffset = scrollOffset;
      var cardScrollLeft = isRtl ? cardRight - width + cardOffset : cardLeft - cardOffset;
      var halfOffsetWidth = offsetWidth / 2;
      var isLastPage = isRtl ? cardScrollLeft <= scrollMax : cardScrollLeft >= scrollMax;
      var isLastCard = isRtl ? cardLeft <= end + halfOffsetWidth : cardRight >= end - halfOffsetWidth;
      var pageScrollLeft = 0;
      if (isLastPage) {
        if (isLastCard) {
          pageScrollLeft = scrollMax;
        }
      } else if (pageWidth === 'card') {
        pageScrollLeft = cardScrollLeft;
        cardPageIndex += 1;
      } else {
        var pageWidthAuto = (isRtl ? width - cardLeft : cardRight) - pageOffset;
        if (pageWidthAuto > width - widthOffset) {
          pageScrollLeft = cardScrollLeft;
          cardPageIndex += 1;
        }
      }
      if (pageScrollLeft) {
        pagesScrollLeftRef.current.push(pageScrollLeft);
        snapOffsetsRef.current.push(pageScrollLeft - (pageScrollLeft - pageOffset) / 2);
        card.classList.remove(scrollSnapNoneClass);
        pageOffset = pageScrollLeft;
      } else {
        card.classList.add(scrollSnapNoneClass);
      }
      cardsScrollLeftRef.current[cardIndex] = isLastPage ? scrollMax : pageOffset;
      cardsPageRef.current[cardIndex] = cardPageIndex + +isLastPage;
    }
    var pageLength = pagesScrollLeftRef.current.length;
    pageIndex = pagesScrollLeftRef.current.findIndex(function (offset) {
      return offset >= scrollLeft;
    });
    setNavigatorVisibility(pageIndex, pageLength);
    setLength(pageLength);
  }, [hasNavigator, isRtl, pageWidth, setNavigatorVisibility, scrollOffset]);
  var resizeObserverRef = React.useRef();
  var intersectionObserverRef = React.useRef();
  var disconnectObservers = function disconnectObservers() {
    var _resizeObserverRef$cu, _intersectionObserver;
    (_resizeObserverRef$cu = resizeObserverRef.current) === null || _resizeObserverRef$cu === void 0 || _resizeObserverRef$cu.disconnect();
    (_intersectionObserver = intersectionObserverRef.current) === null || _intersectionObserver === void 0 || _intersectionObserver.disconnect();
  };
  var attachObservers = function attachObservers(element) {
    var _resizeObserverRef$cu2, _intersectionObserver2;
    (_resizeObserverRef$cu2 = resizeObserverRef.current) === null || _resizeObserverRef$cu2 === void 0 || _resizeObserverRef$cu2.observe(element);
    (_intersectionObserver2 = intersectionObserverRef.current) === null || _intersectionObserver2 === void 0 || _intersectionObserver2.observe(element);
  };
  var computeIndex = React.useCallback(function () {
    if (!carouselRef.current) {
      return;
    }
    var scrollLeft = carouselRef.current.scrollLeft;
    var snapOffsets = snapOffsetsRef.current;
    var pageLength = snapOffsets.length;
    var pageIndex = 0;
    cardScrollToRef.current = undefined;
    for (var snapIndex = 0; !!Math.floor(scrollLeft) && snapIndex < snapOffsets.length; snapIndex += 1) {
      var snapOffset = snapOffsets[snapIndex] - scrollLeft;
      if (scrollLeft < 0 && snapOffset <= 0 || scrollLeft > 0 && snapOffset >= 0) break;
      pageIndex = snapIndex;
    }
    cardRef.current = cardsPageRef.current[pageIndex];
    setNavigatorVisibility(pageIndex, pageLength);
    setIndex(pageIndex);
    var scrollDirectionModifier = direction === Direction.RTL ? -1 : 1;
    var scrollDelta = (scrollLeft - lastScrollLeftRef.current) * scrollDirectionModifier;
    var scrollDirection = scrollDelta > 0 ? 'next' : 'previous';
    onScroll === null || onScroll === void 0 || onScroll(pageIndex, pageLength, scrollLeft, scrollDirection);
    lastScrollLeftRef.current = scrollLeft;
  }, [direction, onScroll, setNavigatorVisibility]);
  var handleScrollEnd = React.useCallback(function () {
    isScrollingRef.current = false;
  }, []);
  var onScrollEndPolyFills = React.useCallback(function () {
    if (scrollEndTimerRef.current) {
      var _scrollEndTimerRef$cu;
      (_scrollEndTimerRef$cu = scrollEndTimerRef.current) === null || _scrollEndTimerRef$cu === void 0 || _scrollEndTimerRef$cu.destroy();
    }
    scrollEndTimerRef.current = createTimer({
      callback: handleScrollEnd,
      delay: scrollEndTimer
    });
  }, [scrollEndTimerRef, handleScrollEnd]);
  var handleScroll = React.useCallback(function () {
    computeIndex();
    // call onscrollend poly fills for browsers that do not support it
    if (!('onscrollend' in window)) {
      onScrollEndPolyFills();
    }
  }, [computeIndex, onScrollEndPolyFills]);
  var createCarouselRef = React.useCallback(function (carousel) {
    if (!carousel) {
      disconnectObservers();
      return;
    }
    var onResizeDebounce = createDebouncedFunction(computePagination, resizeDebounce);
    var onScrollDebounce = createDebouncedFunction(handleScroll, scrollDebounce);
    var onScrollEndDebounce = createDebouncedFunction(handleScrollEnd, scrollDebounce);
    carouselRef.current = carousel;
    childrenRef.current = [];
    if (isSingle && loading === 'lazy') {
      intersectionObserverRef.current = new IntersectionObserver(function (entries) {
        return setIntersection(entries);
      }, {
        root: carousel
      });
    }
    resizeObserverRef.current = new ResizeObserverPolyfill(function () {
      return onResizeDebounce();
    });
    resizeObserverRef.current.observe(carousel);
    Array.from(carousel.children).forEach(function (child, childIndex) {
      childrenRef.current[childIndex] = child;
      attachObservers(child);
    });
    carousel.addEventListener('scroll', onScrollDebounce);
    carousel.addEventListener('scrollend', onScrollEndDebounce);
  }, [computePagination, isSingle, loading, handleScrollEnd, handleScroll]);
  var blockArrowKeyDown = React.useCallback(function (event) {
    if ((event === null || event === void 0 ? void 0 : event.key) === 'ArrowLeft') {
      event.preventDefault();
      event.stopPropagation();
    }
    if ((event === null || event === void 0 ? void 0 : event.key) === 'ArrowRight') {
      event.preventDefault();
      event.stopPropagation();
    }
  }, []);
  var scrollTo = React.useCallback(function (left) {
    var carousel = carouselRef.current;
    if (!carousel || left === undefined) return;
    try {
      isScrollingRef.current = true;
      carousel.scroll({
        behavior: 'smooth',
        left: left
      });
    } catch (_unused) {
      carousel.scroll(left, 0);
    }
  }, []);
  var navigate = React.useCallback(function (navigation) {
    if (!hasCarousel || navigation === index || isScrollingRef.current) {
      return;
    }
    var isBackward = navigation === 'backward';
    var isForward = navigation === 'forward';
    var scrollLeftIndex;
    if (isBackward) {
      if (!index) return;
      if (index === 1) {
        var _outerCarouselRef$cur;
        setBackwardVisibility(false);
        (_outerCarouselRef$cur = outerCarouselRef.current) === null || _outerCarouselRef$cur === void 0 || _outerCarouselRef$cur.focus();
      }
      scrollLeftIndex = index - 1;
    } else if (isForward) {
      if (index === length - 1) return;
      if (index === length - 2) {
        var _outerCarouselRef$cur2;
        setForwardVisibility(false);
        (_outerCarouselRef$cur2 = outerCarouselRef.current) === null || _outerCarouselRef$cur2 === void 0 || _outerCarouselRef$cur2.focus();
      }
      scrollLeftIndex = index + 1;
    } else {
      scrollLeftIndex = navigation;
    }
    var scrollLeft = pagesScrollLeftRef.current[scrollLeftIndex];
    onPageChange === null || onPageChange === void 0 || onPageChange(scrollLeftIndex);
    scrollTo(scrollLeft);
  }, [hasCarousel, index, length, scrollTo, onPageChange]);
  var navigatorElement = React.useCallback(function (navigation, accessibilityNavLabel, navAttributes, visible, enabled) {
    if (hasTouchSupport || !pageButtons || !enabled) return null;
    var isForward = navigation === 'forward';
    var width = "".concat(navigatorWidth, "px");
    return /*#__PURE__*/React.createElement(NavigatorMemoized, _extends({
      acceptPointer: true,
      accessibilityLabel: accessibilityNavLabel,
      align: isForward ? 'end' : 'start',
      attributes: navAttributes,
      mb: pageControl === 'bottom' ? '0' : '24'
    }, !isSingle && {
      pb: '16'
    }, {
      disabled: !visible,
      height: "100%",
      px: "4",
      size: "m",
      onClick: function onClick() {
        return navigate(navigation);
      },
      variant: pageButtons || 'inside',
      visible: visible,
      width: width,
      zIndex: 1,
      onKeyDown: blockArrowKeyDown
    }));
  }, [isSingle, hasTouchSupport, navigate, pageButtons, pageControl, blockArrowKeyDown]);
  var pageControlElement = React.useMemo(function () {
    if (!pageControl || length < 2) return null;
    var isBottom = pageControl === 'bottom';
    var size = mediaQuery.l ? 'm' : 's';
    var color = pageControl === 'bottom' ? 'dark' : 'default';
    return /*#__PURE__*/React.createElement(Box, _extends({
      mt: size === 's' ? '8' : '12',
      bottom: "0",
      width: "100%"
    }, isBottom && {
      position: 'absolute',
      mb: '24'
    }), /*#__PURE__*/React.createElement(PageControl, {
      current: index,
      pages: length,
      onClick: function onClick(page) {
        return navigate(page);
      },
      size: size,
      color: color,
      accessibilityPageNumberLabel: accessibilityPageNumberLabel
    }));
  }, [accessibilityPageNumberLabel, mediaQuery.l, index, length, pageControl, navigate]);
  var carouselElement = React.useMemo(function () {
    return /*#__PURE__*/React.createElement(CarouselStyled, {
      ref: createCarouselRef,
      display: "flex",
      overflowX: "auto",
      overflowY: "hidden",
      mb: !isSingle && pageControl === 'below' ? '-16' : undefined,
      $isRtl: isRtl,
      $scrollPaddingStart: scrollOffset,
      $fade: !hasTouchSupport && fade && fadeOption(backward, forward),
      $isSingle: isSingle,
      $scrollSnap: true,
      "data-item-id": "carousel-card-container",
      height: height,
      onKeyDown: blockArrowKeyDown
    }, carouselChildren);
  }, [backward, createCarouselRef, carouselChildren, fade, forward, scrollOffset, isRtl, isSingle, hasTouchSupport, height, blockArrowKeyDown, pageControl]);
  var pageCountElement = React.useMemo(function () {
    if (!pageCount) return null;
    var isBelow = pageControl === 'below';
    var itemCount = "".concat(index + 1, "/").concat(length);
    return /*#__PURE__*/React.createElement(Box, _extends({
      pointerEvents: "none",
      display: "flex",
      position: "absolute",
      bottom: "0",
      start: "0",
      end: "0",
      mx: "16",
      justifyContent: pageCountLocation(pageCount)
    }, isBelow ? {
      mb: '24',
      pb: '16'
    } : _objectSpread({
      mb: '16'
    }, !isSingle && {
      pb: '8'
    })), /*#__PURE__*/React.createElement(PageCount, {
      theme: "on-light",
      itemCount: itemCount
    }));
  }, [index, isSingle, length, pageControl, pageCount]);
  var handleTouchStart = React.useCallback(function (e) {
    if (e.targetTouches && e.targetTouches.length > 0) {
      touchStartXRef.current = e.targetTouches[0].clientX;
      isTouchActiveRef.current = true; // Flag to indicate that touch is in progress
    }
  }, []);
  var handleTouchMove = React.useCallback(function (e) {
    if (isTouchActiveRef.current && e.cancelable && e.targetTouches && e.targetTouches.length > 0) {
      var touchCurrentX = e.targetTouches[0].clientX;
      var moveDistance = touchStartXRef.current - touchCurrentX;
      if (Math.abs(moveDistance) > TOUCH_MOVE_THRESHOLD) {
        e.preventDefault();
        e.stopPropagation();
      }
    }
  }, []);
  var handleTouchEnd = React.useCallback(function (e) {
    if (isTouchActiveRef.current && e.changedTouches && e.changedTouches.length > 0) {
      var touchEndX = e.changedTouches[0].clientX;
      var touchDeltaX = touchStartXRef.current - touchEndX;
      if (touchDeltaX > TOUCH_MOVE_THRESHOLD) {
        // Threshold for swipe detection
        // Swipe to the left, navigate to next slide
        navigate('forward');
      } else if (touchDeltaX < -TOUCH_MOVE_THRESHOLD) {
        // Threshold for swipe detection
        // Swipe to the right, navigate to previous slide
        navigate('backward');
      }

      // Reset
      touchStartXRef.current = 0;
      isTouchActiveRef.current = false; // Reset the flag as touch is finished
    }
  }, [navigate]);
  var backwardNavigator = navigatorElement('backward', accessibilityPreviousNavLabel, previousNavAttributes, backwardVisible, backward);
  var forwardNavigator = navigatorElement('forward', accessibilityNextNavLabel, nextNavAttributes, forwardVisible, forward);
  var handleKeyDown = React.useCallback(function (event) {
    if ((event === null || event === void 0 ? void 0 : event.key) === 'ArrowLeft') {
      navigate(isRtl ? 'forward' : 'backward');
    }
    if ((event === null || event === void 0 ? void 0 : event.key) === 'ArrowRight') {
      navigate(isRtl ? 'backward' : 'forward');
    }
  }, [isRtl, navigate]);
  React.useEffect(function () {
    if (carouselRef.current) {
      var carousel = carouselRef.current;
      carousel.addEventListener('touchstart', handleTouchStart);
      carousel.addEventListener('touchmove', handleTouchMove);
      carousel.addEventListener('touchend', handleTouchEnd);
    }
    return function () {
      var _carouselRef$current, _carouselRef$current2, _carouselRef$current3;
      carouselRef === null || carouselRef === void 0 || (_carouselRef$current = carouselRef.current) === null || _carouselRef$current === void 0 || _carouselRef$current.removeEventListener('touchstart', handleTouchStart);
      carouselRef === null || carouselRef === void 0 || (_carouselRef$current2 = carouselRef.current) === null || _carouselRef$current2 === void 0 || _carouselRef$current2.removeEventListener('touchmove', handleTouchMove);
      carouselRef === null || carouselRef === void 0 || (_carouselRef$current3 = carouselRef.current) === null || _carouselRef$current3 === void 0 || _carouselRef$current3.removeEventListener('touchend', handleTouchEnd);
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd]);
  React.useEffect(function () {
    var carousel = carouselRef.current;
    if (!carousel) return;
    computePagination();
    scrollTo(cardScrollToRef.current);
  }, [computePagination, children, scrollTo]);
  React.useEffect(function () {
    if (currentCard === undefined || currentCard === cardRef.current || !hasCarousel) return;
    var carousel = carouselRef.current;
    var scrollLeft = cardsScrollLeftRef.current[currentCard];
    if (scrollLeft !== (carousel === null || carousel === void 0 ? void 0 : carousel.scrollLeft)) {
      cardScrollToRef.current = scrollLeft;
      scrollTo(scrollLeft);
    } else {
      cardRef.current = currentCard;
    }
  }, [currentCard, hasCarousel, scrollTo]);
  React.useEffect(function () {
    computeIndex();
  }, [length, computeIndex]);
  React.useEffect(function () {
    var handleIntersectionObserver = function handleIntersectionObserver(entries) {
      if (incorrectInertAttrOnCarouselWhenResizing) {
        setHiddenCards(function (currentHiddenCards) {
          var _carouselRef$current4;
          var totalCards = ((_carouselRef$current4 = carouselRef.current) === null || _carouselRef$current4 === void 0 ? void 0 : _carouselRef$current4.children.length) || 0;

          // If something seems wrong, don't update - keep current state
          if (totalCards === 0 || entries.length === 0) {
            return currentHiddenCards;
          }
          var cardsNotInView = _toConsumableArray(currentHiddenCards);

          // Ensure the array is properly sized to match the number of cards
          while (cardsNotInView.length < totalCards) {
            cardsNotInView.push(false);
          }
          entries.forEach(function (entry) {
            var _carouselRef$current5;
            var cardIndex = Array.from(((_carouselRef$current5 = carouselRef.current) === null || _carouselRef$current5 === void 0 ? void 0 : _carouselRef$current5.children) || []).indexOf(entry.target);
            if (cardIndex >= 0) {
              cardsNotInView[cardIndex] = !entry.isIntersecting;
            }
          });
          return cardsNotInView;
        });
      } else {
        var cardsNotInView = [];
        entries.forEach(function (entry, entryIndex) {
          cardsNotInView[entryIndex] = !entry.isIntersecting;
        });
        setHiddenCards(cardsNotInView);
      }
    };

    // eslint-disable-next-line compat/compat
    var observer = new IntersectionObserver(handleIntersectionObserver, {
      root: carouselRef.current,
      rootMargin: '0px',
      threshold: 0.5 // 50% of the card should be visible
    });
    if (carouselRef.current) {
      Array.from(carouselRef.current.children).forEach(function (item) {
        observer.observe(item);
      });
    }
    return function () {
      return observer.disconnect();
    };

    // index dependency requires for re-rendering if page index changes
  }, [index, incorrectInertAttrOnCarouselWhenResizing]);
  return /*#__PURE__*/React.createElement(Box, _extends({}, rest, {
    ref: outerCarouselRef,
    position: "relative",
    "aria-label": accessibilityLabel,
    role: "region",
    height: height,
    tabIndex: 0,
    onKeyDown: handleKeyDown,
    overflowX: "hidden",
    pb: pageControl === 'below' ? '4' : undefined
  }), /*#__PURE__*/React.createElement(Box, {
    position: "relative",
    height: height
  }, backwardNavigator, carouselElement, forwardNavigator, pageCountElement), pageControlElement);
};
Carousel.defaultProps = {
  display: 'list',
  loading: 'lazy',
  pageButtons: 'inside',
  pageWidth: 'auto'
};
var CarouselMemoized = /*#__PURE__*/React.memo(Carousel);
CarouselMemoized.displayName = 'Carousel';

export { CarouselMemoized as Carousel, Carousel as CarouselBase };
//# sourceMappingURL=Carousel.js.map
